/**
 * Utility functions for synchronizing Inquiry/Contact data with the
 * CRM Pipeline Board (both Kanban grid and List views).
 *
 * Updates TanStack Query's cache for ['crm-pipeline-board'] directly
 * so that any changes made in the Overview page (Tags, Assigned User,
 * Stage, Sources, Estimated Value, etc.) are immediately reflected
 * without requiring a full board refetch or page reload.
 */

function normalizeSources(sources) {
  if (!Array.isArray(sources) || sources.length === 0) return undefined;
  return sources
    .map((s) => {
      if (typeof s === 'string') return s;
      return s.source_type || s.name || '';
    })
    .filter(Boolean);
}

function normalizeTags(tags) {
  if (tags === undefined || tags === null) return undefined;
  if (Array.isArray(tags)) return tags;
  if (typeof tags === 'string') {
    return tags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean)
      .map((name) => ({ name }));
  }
  return undefined;
}

/**
 * Directly updates an inquiry/contact inside any cached ['crm-pipeline-board']
 * query in TanStack Query.
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {string|number} contactId
 * @param {object} updatedFields
 */
export function syncPipelineBoardContact(
  queryClient,
  contactId,
  updatedFields,
) {
  if (!queryClient || !contactId || !updatedFields) return;
  const targetIdStr = String(contactId);

  // Retrieve all board queries in cache (matching the prefix 'crm-pipeline-board'
  // regardless of companyId string vs number representation)
  const boardQueries = queryClient.getQueriesData({
    queryKey: ['crm-pipeline-board'],
  });

  if (!boardQueries || boardQueries.length === 0) return;

  boardQueries.forEach(([queryKey, oldData]) => {
    if (!oldData) return;

    const isArray = Array.isArray(oldData);
    const rawStages = isArray ? oldData : oldData.results || oldData.stages;

    if (!Array.isArray(rawStages) || rawStages.length === 0) return;

    // Deep clone stages structure to safely modify
    const nextStages = rawStages.map((stage) => {
      const contacts = stage.contacts || stage.leads || stage.items || [];
      return {
        ...stage,
        contacts: [...contacts],
      };
    });

    // Locate the contact across stages
    let foundStageIdx = -1;
    let foundContactIdx = -1;
    let existingContact = null;

    for (let sIdx = 0; sIdx < nextStages.length; sIdx++) {
      const stage = nextStages[sIdx];
      const cIdx = stage.contacts.findIndex(
        (c) =>
          String(c?.id) === targetIdStr ||
          String(c?.contact_id) === targetIdStr,
      );
      if (cIdx !== -1) {
        foundStageIdx = sIdx;
        foundContactIdx = cIdx;
        existingContact = stage.contacts[cIdx];
        break;
      }
    }

    if (!existingContact) {
      // If contact not found in this cached board (e.g. filtered or not loaded),
      // mark the query stale without refetching immediately.
      queryClient.invalidateQueries({
        queryKey,
        refetchType: 'none',
      });
      return;
    }

    // Resolve normalized fields
    const newTags = normalizeTags(updatedFields.tags);
    const newSources =
      normalizeSources(updatedFields.sources) ||
      (updatedFields.source?.source_type
        ? [updatedFields.source.source_type]
        : undefined) ||
      normalizeSources(updatedFields.contactSources);

    const assignedEmployeeId =
      updatedFields.assigned_to_employee_id ??
      updatedFields.assigned_to_id ??
      updatedFields.assignee_id ??
      existingContact.assigned_to_employee_id ??
      existingContact.assigned_to_id;

    const assignedEmployeeName =
      updatedFields.assigned_to_employee_name ??
      updatedFields.assigned_to_name ??
      updatedFields.assignee?.name ??
      existingContact.assigned_to_employee_name ??
      existingContact.assigned_to_name;

    const estimatedValue =
      updatedFields.estimated_value !== undefined &&
      updatedFields.estimated_value !== null
        ? String(updatedFields.estimated_value)
        : existingContact.estimated_value;

    const contactName =
      updatedFields.name ||
      (updatedFields.first_name || updatedFields.last_name
        ? [updatedFields.first_name, updatedFields.last_name]
            .filter(Boolean)
            .join(' ')
        : existingContact.name);

    // Merge updated fields into contact
    const mergedContact = {
      ...existingContact,
      ...updatedFields,
      id: existingContact.id,
      name: contactName,
      phone:
        updatedFields.phone !== undefined
          ? updatedFields.phone
          : existingContact.phone,
      email:
        updatedFields.email !== undefined
          ? updatedFields.email
          : existingContact.email,
      tags: newTags !== undefined ? newTags : existingContact.tags,
      sources: newSources !== undefined ? newSources : existingContact.sources,
      estimated_value: estimatedValue,
      assigned_to_employee_id: assignedEmployeeId,
      assigned_to_id: assignedEmployeeId,
      assigned_to_employee_name: assignedEmployeeName,
      assigned_to_name: assignedEmployeeName,
      assignee:
        updatedFields.assignee ??
        (assignedEmployeeName
          ? { id: assignedEmployeeId, name: assignedEmployeeName }
          : existingContact.assignee),
      custom_fields: updatedFields.custom_fields
        ? {
            ...(existingContact.custom_fields || {}),
            ...updatedFields.custom_fields,
          }
        : existingContact.custom_fields,
      created_at:
        updatedFields.created_at !== undefined
          ? updatedFields.created_at
          : (existingContact.created_at ?? null),
      updated_at: updatedFields.updated_at || new Date().toISOString(),
    };

    // Determine target stage
    const rawTargetStageId =
      updatedFields.target_stage_id ??
      updatedFields.stage_id ??
      (typeof updatedFields.current_stage === 'object'
        ? updatedFields.current_stage?.id
        : updatedFields.current_stage) ??
      (typeof updatedFields.stage === 'object'
        ? updatedFields.stage?.id
        : updatedFields.stage);

    const targetStageIdStr =
      rawTargetStageId != null ? String(rawTargetStageId) : null;
    const currentStageIdStr = String(nextStages[foundStageIdx].id);

    const isStageChanged =
      targetStageIdStr != null && targetStageIdStr !== currentStageIdStr;

    if (isStageChanged) {
      // 1. Remove contact from previous stage
      const oldStage = nextStages[foundStageIdx];
      oldStage.contacts.splice(foundContactIdx, 1);

      if (typeof oldStage.count === 'number') {
        oldStage.count = Math.max(0, oldStage.count - 1);
      }

      const oldVal = parseFloat(existingContact.estimated_value);
      if (!isNaN(oldVal) && oldStage.total_estimated_value != null) {
        const curTotal = parseFloat(oldStage.total_estimated_value || '0');
        oldStage.total_estimated_value = String(
          Math.max(0, curTotal - oldVal).toFixed(2),
        );
      }

      // 2. Add to target stage
      const targetStage = nextStages.find(
        (s) => String(s.id) === targetStageIdStr,
      );

      if (targetStage) {
        mergedContact.current_stage = {
          id: targetStage.id,
          name: targetStage.name,
          color: targetStage.color,
        };
        mergedContact.stage = mergedContact.current_stage;

        targetStage.contacts.push(mergedContact);

        if (typeof targetStage.count === 'number') {
          targetStage.count = (targetStage.count || 0) + 1;
        }

        const newVal = parseFloat(mergedContact.estimated_value);
        if (!isNaN(newVal) && targetStage.total_estimated_value != null) {
          const curTotal = parseFloat(targetStage.total_estimated_value || '0');
          targetStage.total_estimated_value = String(
            (curTotal + newVal).toFixed(2),
          );
        }
      }
    } else {
      // Stage did not change - update in place
      const curStage = nextStages[foundStageIdx];
      curStage.contacts[foundContactIdx] = mergedContact;

      // Adjust total_estimated_value if estimated_value changed
      const oldVal = parseFloat(existingContact.estimated_value);
      const newVal = parseFloat(mergedContact.estimated_value);
      if (
        !isNaN(newVal) &&
        !isNaN(oldVal) &&
        newVal !== oldVal &&
        curStage.total_estimated_value != null
      ) {
        const curTotal = parseFloat(curStage.total_estimated_value || '0');
        curStage.total_estimated_value = String(
          Math.max(0, curTotal - oldVal + newVal).toFixed(2),
        );
      }
    }

    // Apply updated stages to query cache
    queryClient.setQueryData(queryKey, () => {
      if (isArray) {
        return nextStages;
      }
      return {
        ...oldData,
        results: nextStages,
        stages: nextStages,
      };
    });
  });
}

/**
 * Helper to sync board data using cached contact details query if available.
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {string|number} contactId
 */
export function syncPipelineBoardFromCache(queryClient, contactId) {
  if (!queryClient || !contactId) return;
  const targetIdStr = String(contactId);

  // Look for any cached crm-contact-detail query for this contactId
  const detailQueries = queryClient.getQueriesData({
    queryKey: ['crm-contact-detail'],
  });

  const matchingQuery = detailQueries.find(([key, data]) => {
    return (
      data &&
      (String(data.id) === targetIdStr ||
        key.some((k) => String(k) === targetIdStr))
    );
  });

  if (matchingQuery && matchingQuery[1]) {
    const contactData = matchingQuery[1];
    // Also look for cached contact sources
    const sourcesQueries = queryClient.getQueriesData({
      queryKey: ['crm-contact-sources'],
    });
    const sourcesQuery = sourcesQueries.find(([key]) =>
      key.some((k) => String(k) === targetIdStr),
    );
    const sourcesData = sourcesQuery?.[1];

    syncPipelineBoardContact(queryClient, contactId, {
      ...contactData,
      sources: sourcesData,
    });
  }
}
